import { useInfiniteQuery } from '@tanstack/react-query'
import {
  OkendoResponse,
  ReviewAggregate,
  ReviewsResponse,
  ReviewsSummary,
  useReviewsQueryOptions,
} from './types'
import { useShopify } from '@/hooks/useShopify'

export const useReviewsQuery = ({
  productId,
  targetPageSize = 24,
  orderBy = 'rating%20desc',
  ...options
}: useReviewsQueryOptions) => {
  const { shop, integrations, locale: baseLocale } = useShopify()
  const { okendo_user_id } = integrations

  const { data: { pages = [] } = {}, ...result } = useInfiniteQuery({
    queryKey: ['reviews', productId, shop],
    queryFn: async ({ pageParam }): Promise<ReviewsResponse> => {
      const locale = baseLocale.includes('-')
        ? baseLocale.split('-')[0]
        : baseLocale

      // Fetch from Okendo API
      const baseUrl = `https://api.okendo.io/v1`

      const aggregateRes = await fetch(
        `${baseUrl}/stores/${okendo_user_id}/products/shopify-${productId}/review_aggregate`,
      )
      const { reviewAggregate } = (await aggregateRes.json()) as {
        reviewAggregate: ReviewAggregate
      }

      let reviewsSummaryData: ReviewsSummary | undefined
      try {
        const summaryRes = await fetch(
          `${baseUrl}/stores/${okendo_user_id}/products/shopify-${productId}/reviews_summary?locale=${locale}`,
        )
        const { reviewsSummary } = (await summaryRes.json()) as {
          reviewsSummary: ReviewsSummary
        }
        reviewsSummaryData = reviewsSummary
      } catch (error) {
        console.error(error)
        reviewsSummaryData = undefined
      }

      const url = pageParam
        ? `${baseUrl}${pageParam}`
        : `${baseUrl}/stores/${okendo_user_id}/products/shopify-${productId}/reviews?limit=${targetPageSize}&locale=${locale}&orderBy=${orderBy}`
      const res = await fetch(url)
      const data = (await res.json()) as Omit<OkendoResponse, 'next'>

      const reviewsData = {
        reviews:
          data.reviews?.map((review) => {
            return {
              id: review.reviewId,
              rating: review.rating,
              title: review.title,
              body: review.body,
              dateCreated: review.dateCreated,
              reviewer: {
                author: review.reviewer?.displayName,
                isVerified: review.reviewer.isVerified ?? false,
              },
              original: review?.original,
            }
          }) ?? [],
        cursor: data.nextUrl,
        total: reviewAggregate.reviewCount,
        attributeRatings: reviewAggregate.attributeRatings,
        summary: reviewsSummaryData?.body,
      } as ReviewsResponse
      return reviewsData
    },
    initialPageParam: undefined as ReviewsResponse['cursor'],
    getNextPageParam: (lastPage: ReviewsResponse) => lastPage.cursor,
    ...options,
  })
  return { data: { pages }, ...result }
}
